import React from 'react';
import { BrowserRouter as Router, Route, Navigate } from 'react-router-dom';
import { FreshRoutes } from './ui/AppShell/freshNav';
import { AuthProvider } from './contexts/AuthContext';
import { ModalProvider } from './contexts/ModalContext'; // NEW
import GlobalModal from './components/GlobalModal'; // NEW
import ProtectedRoute from './components/ProtectedRoute'; // Ensure this component exists
import HomeRedirect from './components/HomeRedirect';
import Login from './pages/Login';
import StaffManagementPortal from './pages/StaffManagementPortal/StaffManagementPortal';
import FleetManager from './pages/FleetManager/FleetManager';
import Dashboard from './pages/Dashboard';
import AssistantPage from './pages/AssistantPage';
import Modules from './pages/Modules/Modules';
import KPIModule from './pages/KPIModule/KPIModule';

import RoleManagement from './pages/RoleManagement/RoleManagement';
import ContractQuickActions from './components/ContractQuickActions';
import PageViewLogger from './components/PageViewLogger';

import InvoiceGenerator from './pages/InvoiceGenerator/InvoiceGenerator';
import OperationsPortal from './pages/OperationsPortal/OperationsPortal';
import BranchCharges from './pages/BranchCharges/BranchCharges';
import DailyBookingReport from './pages/DailyBookingReport/DailyBookingReport';
import UserManagement from './pages/UserManagement/UserManagement';
import ContractsManagement from './pages/ContractsManagement/ContractsManagement';
import CarsMaintenance from './pages/CarsMaintenance/CarsMaintenance';
import HistoryLens from './pages/HistoryLens/HistoryLens';
import ChangePasswordPage from './pages/ChangePasswordPage';
import CarUsage from './pages/CarUsage/CarUsage';
import CustodiesDashboard from './pages/Custodies/CustodiesDashboard'; // NEW
import CustodyDetails from './pages/Custodies/CustodyDetails'; // NEW
import FleetServiceHistory from './pages/FleetServiceHistory/FleetServiceHistory';
import AirportDashboardPage from './pages/AirportDashboard/AirportDashboardPage'; // NEW
import GovernmentTransactions, { OLD_LINKS, govTabPath } from './pages/GovernmentTransactions/GovernmentTransactions';
import TrafficFines from './pages/TrafficFines/TrafficFines';
import Database from './pages/Database/Database';
import ImporterPage from './pages/Database/ImporterPage';
import { PERMISSIONS } from './constants/permissions';
import TheEngine from './pages/the-engine/TheEngine';
import AutomationPage from './pages/Automation/AutomationPage';
import RentwayPage from './pages/Rentway/RentwayPage';
import AvailabilityPage from './pages/Availability/AvailabilityPage';
import SectionPage from './pages/Section/SectionPage';

// ...




import { ThemeProvider } from './theme';

// Design-system gallery with sample data: development builds only.
const DesignSystemPreview = React.lazy(() => import('./pages/DesignSystemPreview'));
const IS_DEV = process.env.NODE_ENV === 'development';

function App() {
    return (
        <ThemeProvider>
            <ModalProvider>
                <AuthProvider>
                    <Router>
                        <GlobalModal />
                        <ContractQuickActions />
                        <PageViewLogger />
                        <FreshRoutes>
                            <Route path="/login" element={<Login />} />

                            <Route path="/change-password" element={
                                <ProtectedRoute>
                                    <ChangePasswordPage />
                                </ProtectedRoute>
                            } />

                            {/* Protected Routes */}
                            <Route path="/staff" element={
                                <ProtectedRoute requiredPermission={PERMISSIONS.view_staff}>
                                    <StaffManagementPortal />
                                </ProtectedRoute>
                            } />

                            <Route path="/dashboard" element={
                                <ProtectedRoute requiredPermission={PERMISSIONS.view_dashboard}>
                                    <Dashboard />
                                </ProtectedRoute>
                            } />

                            {/* the assistant on its own page: its permission alone, no Overview (owner, 5 Oct 2026) */}
                            <Route path="/assistant" element={
                                <ProtectedRoute requiredPermission={PERMISSIONS.ask_assistant}>
                                    <AssistantPage />
                                </ProtectedRoute>
                            } />

                            {/* every page as a card, in the user's order (owner, 1 Oct 2026: its own page in the sidebar) */}
                            <Route path="/modules" element={
                                <ProtectedRoute>
                                    <Modules />
                                </ProtectedRoute>
                            } />

                            <Route path="/kpis" element={
                                <ProtectedRoute requiredPermission={PERMISSIONS.view_kpi_dashboard}>
                                    <KPIModule />
                                </ProtectedRoute>
                            } />

                            <Route path="/invoices" element={
                                <ProtectedRoute requiredPermission={PERMISSIONS.view_invoices}>
                                    <InvoiceGenerator />
                                </ProtectedRoute>
                            } />

                            <Route path="/operations" element={
                                <ProtectedRoute requiredPermission={PERMISSIONS.view_operations}>
                                    <OperationsPortal />
                                </ProtectedRoute>
                            } />

                            <Route path="/fleet" element={
                                <ProtectedRoute requiredPermission={PERMISSIONS.view_fleet_list}>
                                    <FleetManager />
                                </ProtectedRoute>
                            } />

                            <Route path="/contracts" element={
                                <ProtectedRoute requiredPermission={PERMISSIONS.view_contracts}>
                                    <ContractsManagement />
                                </ProtectedRoute>
                            } />

                            <Route path="/maintenance" element={
                                <ProtectedRoute requiredPermission={PERMISSIONS.view_maintenance}>
                                    <CarsMaintenance />
                                </ProtectedRoute>
                            } />

                            <Route path="/daily-booking" element={
                                <ProtectedRoute requiredPermission={PERMISSIONS.view_daily_booking}>
                                    <DailyBookingReport />
                                </ProtectedRoute>
                            } />

                            {/* Daily Booking Management was merged into the Daily Rental Report */}
                            <Route path="/daily-booking-ops" element={<Navigate to="/daily-booking" replace />} />

                            <Route path="/admin/users" element={
                                <ProtectedRoute requiredPermission={PERMISSIONS.manage_users}>
                                    <UserManagement />
                                </ProtectedRoute>
                            } />

                            <Route path="/admin/roles" element={
                                <ProtectedRoute requiredPermission={PERMISSIONS.manage_roles}>
                                    <RoleManagement />
                                </ProtectedRoute>
                            } />

                            {/* Multi-car is a tab of History Lens now: the old address lands on it */}
                            <Route path="/multi-car" element={<Navigate to="/report-history?tab=multicar" replace />} />

                            <Route path="/branch-charges" element={
                                <ProtectedRoute requiredPermission={PERMISSIONS.view_branch_charges}>
                                    <BranchCharges />
                                </ProtectedRoute>
                            } />

                            <Route path="/report-history" element={
                                <ProtectedRoute requiredPermission={PERMISSIONS.view_history}>
                                    <HistoryLens />
                                </ProtectedRoute>
                            } />
                            <Route path="/car-usage" element={
                                <ProtectedRoute requiredPermission={PERMISSIONS.view_matcher}>
                                    <CarUsage />
                                </ProtectedRoute>
                            } />
                            {/* the Matcher Master became Car usage (automatic): the old address lands there */}
                            <Route path="/matcher-master" element={<Navigate to="/car-usage" replace />} />

                            {/* Petty Cash Module */}
                            <Route path="/custodies" element={
                                <ProtectedRoute requiredPermission={PERMISSIONS.view_custodies}>
                                    <CustodiesDashboard />
                                </ProtectedRoute>
                            } />
                            <Route path="/custodies/:id" element={
                                <ProtectedRoute requiredPermission={PERMISSIONS.view_custodies}>
                                    <CustodyDetails />
                                </ProtectedRoute>
                            } />

                            {/* Fleet Service History */}
                            <Route path="/fleet-service-history" element={
                                <ProtectedRoute requiredPermission={PERMISSIONS.view_service_history}>
                                    <FleetServiceHistory />
                                </ProtectedRoute>
                            } />

                            {/* Airport Dashboard */}
                            <Route path="/airport-dashboard" element={
                                <ProtectedRoute requiredPermission={PERMISSIONS.view_airport_dashboard}>
                                    <AirportDashboardPage />
                                </ProtectedRoute>
                            } />

                            <Route path="/government" element={
                                <ProtectedRoute requiredPermission={PERMISSIONS.view_government_transactions}>
                                    <GovernmentTransactions />
                                </ProtectedRoute>
                            } />

                            {/* the letters + sales invoice are tabs of /government now (18 Oct 2026); the old links open their tab */}
                            {Object.entries(OLD_LINKS).map(([path, tab]) => (
                                <Route key={path} path={path} element={<Navigate to={govTabPath(tab)} replace />} />
                            ))}



                            {/* Default Redirect */}
                            <Route path="/traffic-fines" element={
                                <ProtectedRoute requiredPermission={PERMISSIONS.view_traffic_fines}>
                                    <TrafficFines />
                                </ProtectedRoute>
                            } />

                            <Route path="/the-engine" element={
                                <ProtectedRoute requiredPermission={PERMISSIONS.view_engine}>
                                    <TheEngine />
                                </ProtectedRoute>
                            } />

                            <Route path="/automation" element={
                                <ProtectedRoute requiredPermission={PERMISSIONS.use_automation}>
                                    <AutomationPage />
                                </ProtectedRoute>
                            } />

                            {/* Operations > Brokers (was "Rentway" - owner, 23 Oct 2026): the booking sites' reservations (Rentway + Comfy)
                                vs Ejar (a catalogue key, the admin's until a role gets it); the old address still opens it */}
                            <Route path="/brokers" element={
                                <ProtectedRoute requiredPermission="view_ops_rentway">
                                    <RentwayPage />
                                </ProtectedRoute>
                            } />
                            <Route path="/rentway" element={<Navigate to="/brokers" replace />} />
                            {/* Operations > Availability (owner, 24 Oct 2026): every airport model's cars free day by day - was the
                                Brokers page's tab (a catalogue key, the admin's until a role gets it) */}
                            <Route path="/availability" element={
                                <ProtectedRoute requiredPermission="view_ops_availability">
                                    <AvailabilityPage />
                                </ProtectedRoute>
                            } />

                            <Route path="/database" element={
                                <ProtectedRoute requiredPermission={PERMISSIONS.view_database}>
                                    <Database />
                                </ProtectedRoute>
                            } />
                            <Route path="/database/:importerId" element={
                                <ProtectedRoute requiredPermission={PERMISSIONS.view_database}>
                                    <ImporterPage />
                                </ProtectedRoute>
                            } />

                            {/* Section pages: all modules of a sidebar group (breadcrumb targets) */}
                            <Route path="/section/:groupId" element={
                                <ProtectedRoute>
                                    <SectionPage />
                                </ProtectedRoute>
                            } />

                            {IS_DEV && (
                                <Route path="/design-system" element={
                                    <React.Suspense fallback={null}>
                                        <DesignSystemPreview />
                                    </React.Suspense>
                                } />
                            )}

                            {/* home: the first page the role opens (the Overview only for a role that has it) */}
                            <Route path="/" element={<ProtectedRoute><HomeRedirect /></ProtectedRoute>} />

                        </FreshRoutes>
                    </Router>
                </AuthProvider>
            </ModalProvider>
        </ThemeProvider>
    );
}

export default App;
